import { z } from 'zod'

const JwtPayloadSchema = z.object({
	guid: z.string().optional(),
	user: z
		.object({
			guid: z.string(),
			email: z.string().optional(),
			phone: z.string().optional(),
			name: z.string().optional(),
		})
		.optional(),
	version: z.string().optional(),
	iat: z.number().optional(),
	exp: z.number().optional(),
})

export type JwtPayload = z.infer<typeof JwtPayloadSchema>

export function decodeJwtPayload(token: string): JwtPayload {
	const tokenParts = token.split('.')
	if (tokenParts.length !== 3) {
		throw new Error('Invalid token format')
	}

	let base64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/')
	// Restore padding (JWT base64url omits = padding, but atob requires length % 4 === 0)
	base64 += '='.repeat((4 - (base64.length % 4)) % 4)

	let jsonStr: string
	try {
		jsonStr = decodeURIComponent(
			atob(base64)
				.split('')
				.map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
				.join(''),
		)
	} catch {
		throw new Error('[jwtDecode] Failed to decode access token payload')
	}

	let parsed: unknown
	try {
		parsed = JSON.parse(jsonStr)
	} catch {
		throw new Error('[jwtDecode] Failed to parse token payload JSON')
	}

	const result = JwtPayloadSchema.safeParse(parsed)

	if (!result.success) {
		throw new Error(`Invalid JWT payload: ${result.error.message}`)
	}

	return result.data
}
